import qs from 'qs';
import useSWR from 'swr';

import type * as Type from '@/common/interface';
import request from '@/utils/request';

export type BridgeNodeRole = 'front' | 'backend';
export type BridgeNodeState =
  | 'draft'
  | 'verifying'
  | 'enabled'
  | 'draining'
  | 'disabled';

export type BridgeNodeItem = {
  id: string;
  numeric_id: number;
  name: string;
  region_code: string;
  provider: string;
  relay_role: BridgeNodeRole;
  relay_group_id: string;
  relay_backend_id: string;
  front_mode: string;
  backend_mode: string;
  client_visible: boolean;
  entry_host: string;
  entry_port: number;
  node_type: string;
  core_type: string;
  transport: string;
  security: string;
  server_name: string;
  base_config: Record<string, unknown>;
  control_token_prefix: string;
  control_token_version: number;
  config_version: number;
  policy_ack_version: number;
  policy_ack_hash: string;
  status: BridgeNodeState;
  capacity_limit: number;
  weight: number;
  agent_version: string;
  agent_capabilities: string[];
  guard_state: string;
  last_error_code: string;
  last_seen_at?: string;
  last_config_ack_at?: string;
  realtime_metric?: BridgeNodeRealtimeMetric;
  created_at: string;
  updated_at: string;
};

export type BridgeNodeRealtimeMetric = {
  reported_at: string;
  interval_seconds: number;
  upload_bytes: number;
  download_bytes: number;
  upload_per_second: number;
  download_per_second: number;
  connections: number;
  online_users: number;
  cpu_percent: number;
  memory_bytes: number;
  allowed_count: number;
  rejected_count: number;
  reject_reasons?: string;
  config_version: number;
  policy_version: number;
  policy_stale: boolean;
};

export type BridgeNodeMetricPoint = {
  minute_at: string;
  upload_bytes: number;
  download_bytes: number;
  upload_per_second: number;
  download_per_second: number;
  connections: number;
  online_users: number;
  cpu_percent: number;
  memory_bytes: number;
  allowed_count: number;
  rejected_count: number;
  policy_version: number;
  policy_stale: boolean;
};

export type BridgeNodeMetrics = {
  node_id: string;
  range_seconds: number;
  start_at: string;
  end_at: string;
  latest?: BridgeNodeRealtimeMetric;
  summary: {
    upload_bytes: number;
    download_bytes: number;
    rejected_count: number;
  };
  points: BridgeNodeMetricPoint[];
  traffic_stats: {
    total_upload_bytes: number;
    total_download_bytes: number;
    daily: Array<{
      period: string;
      upload_bytes: number;
      download_bytes: number;
    }>;
    hourly: Array<{
      period: string;
      upload_bytes: number;
      download_bytes: number;
    }>;
  };
};

export type BridgeNodeSaveParams = {
  name: string;
  region_code: string;
  provider: string;
  relay_role: BridgeNodeRole;
  relay_group_id: string;
  relay_backend_id: string;
  client_visible: boolean;
  entry_host: string;
  entry_port: number;
  node_type: string;
  core_type: string;
  transport: string;
  security: string;
  server_name: string;
  base_config: Record<string, unknown>;
  secret_config?: Record<string, unknown>;
  capacity_limit: number;
  weight: number;
};

export type BridgeNodeCreateResponse = {
  node: BridgeNodeItem;
  control_token: string;
  config_snippet: string;
};

export type BridgeRelayGroupItem = {
  id: string;
  name: string;
  status: 'draft' | 'verifying' | 'enabled' | 'disabled';
  relay_server: string;
  backend_id: string;
  front_mode: string;
  backend_mode: string;
  relay_purpose: string;
  business_guard_enabled: boolean;
  guard_profile: string;
  enforcement: string;
  transport_config: Record<string, unknown>;
  domain_policy_id: string;
  active_policy_version: number;
  pending_policy_version: number;
  policy_rollout_state: string;
  created_at: string;
  updated_at: string;
};

export type BridgeRelayGroupSaveParams = {
  name: string;
  status: BridgeRelayGroupItem['status'];
  relay_server: string;
  backend_id: string;
  relay_purpose: string;
  business_guard_enabled: boolean;
  guard_profile: string;
  enforcement: string;
  transport_config: Record<string, unknown>;
  secret_config?: Record<string, unknown>;
  domain_policy_id: string;
};

export type BridgeNodePoolMember = {
  node_id: string;
  weight: number;
  sort: number;
  accept_new_connections: boolean;
};

export type BridgeNodePoolItem = {
  id: string;
  name: string;
  status: 'enabled' | 'disabled';
  selection_policy: 'stable_hash' | 'priority';
  max_nodes: number;
  allowed_platforms: string[];
  min_client_version: string;
  region_priority: string[];
  canary_percent: number;
  version: number;
  members: BridgeNodePoolMember[];
  created_at: string;
  updated_at: string;
};

export type BridgeNodePoolSaveParams = Omit<
  BridgeNodePoolItem,
  'id' | 'version' | 'created_at' | 'updated_at'
>;

export type BridgeDomainRule = {
  host: string;
  match: 'exact' | 'subdomain';
  ports: number[];
  networks: Array<'tcp' | 'udp'>;
  require_tls_sni: boolean;
};

export type BridgeDomainPolicyItem = {
  id: string;
  name: string;
  status: string;
  version: number;
  rules: BridgeDomainRule[];
  created_at: string;
  updated_at: string;
};

export type BridgeDomainPolicySaveParams = {
  name: string;
  rules: BridgeDomainRule[];
};

export type BridgePolicyReleaseItem = {
  id: string;
  relay_group_id: string;
  policy_id: string;
  version: number;
  content_hash: string;
  signing_key_id: string;
  issued_at: string;
  expires_at: string;
  published_by: string;
  status: string;
  source_release_id?: string;
};

export type BridgePolicySigningProfile = {
  ready: boolean;
  algorithm: string;
  public_key_encoding: string;
  public_key: string;
  key_id: string;
  profile: string;
  audience: string;
  schema_version: number;
  max_policy_bytes: number;
};

export type BridgeAccessGrantItem = {
  id: string;
  answer_user_id: string;
  bridge_uid: number;
  credential_type: string;
  credential_version: number;
  pool_id: string;
  access_scope: 'business' | 'login_bootstrap';
  status: 'enabled' | 'disabled' | 'revoked';
  device_limit: number;
  speed_limit: number;
  expires_at?: string;
  reason: string;
  created_by: string;
  created_at: string;
  updated_at: string;
};

export type BridgeAccessGrantSaveParams = {
  answer_user_id: string;
  pool_id: string;
  access_scope: 'business' | 'login_bootstrap';
  status: 'enabled' | 'disabled';
  device_limit: number;
  speed_limit: number;
  expires_at: number;
  reason: string;
};

export type BridgeLoginBootstrapEnvelope = {
  schema_version: 1;
  key_id: string;
  algorithm: 'Ed25519';
  payload_base64: string;
  signature: string;
};

export type BridgeNodeAuditItem = {
  id: number;
  actor_user_id: string;
  action: string;
  object_type: string;
  object_id: string;
  summary: Record<string, unknown>;
  created_at: string;
};

const swr = <T>(apiUrl: string | null, refreshInterval = 0) => {
  const { data, error, mutate } = useSWR<T, Error>(
    apiUrl ? [apiUrl] : null,
    request.instance.get,
    refreshInterval > 0 ? { refreshInterval } : undefined,
  );
  return { data, error, mutate, isLoading: Boolean(apiUrl && !data && !error) };
};

export const useBridgeNodes = (params: {
  page: number;
  page_size: number;
  status?: string;
  relay_role?: string;
  keyword?: string;
}) =>
  swr<Type.ListResult<BridgeNodeItem>>(
    `/answer/admin/api/bridge-nodes?${qs.stringify(params, {
      skipNulls: true,
    })}`,
    10_000,
  );

export const useBridgeNodeMetrics = (id?: string, rangeSeconds = 3600) =>
  swr<BridgeNodeMetrics>(
    id
      ? `/answer/admin/api/bridge-nodes/${id}/metrics?${qs.stringify({
          range_seconds: rangeSeconds,
        })}`
      : null,
    id ? 10_000 : 0,
  );

export const createBridgeNode = (params: BridgeNodeSaveParams) =>
  request.post<BridgeNodeCreateResponse>(
    '/answer/admin/api/bridge-nodes',
    params,
  );
export const updateBridgeNode = (id: string, params: BridgeNodeSaveParams) =>
  request.put<BridgeNodeItem>(`/answer/admin/api/bridge-nodes/${id}`, params);
export const setBridgeNodeState = (id: string, status: BridgeNodeState) =>
  request.post<BridgeNodeItem>(`/answer/admin/api/bridge-nodes/${id}/state`, {
    status,
  });
export const rotateBridgeNodeToken = (id: string) =>
  request.post<BridgeNodeCreateResponse>(
    `/answer/admin/api/bridge-nodes/${id}/rotate-token`,
  );

export const useBridgeRelayGroups = () =>
  swr<BridgeRelayGroupItem[]>('/answer/admin/api/bridge-relay-groups');
export const createBridgeRelayGroup = (params: BridgeRelayGroupSaveParams) =>
  request.post<BridgeRelayGroupItem>(
    '/answer/admin/api/bridge-relay-groups',
    params,
  );
export const updateBridgeRelayGroup = (
  id: string,
  params: BridgeRelayGroupSaveParams,
) =>
  request.put<BridgeRelayGroupItem>(
    `/answer/admin/api/bridge-relay-groups/${id}`,
    params,
  );
export const publishBridgeDomainPolicy = (
  groupId: string,
  policyId: string,
  validFor: string,
) =>
  request.post<BridgePolicyReleaseItem>(
    `/answer/admin/api/bridge-relay-groups/${groupId}/policy-releases`,
    { policy_id: policyId, valid_for: validFor },
  );
export const rollbackBridgeDomainPolicy = (
  groupId: string,
  releaseId: string,
  validFor: string,
) =>
  request.post<BridgePolicyReleaseItem>(
    `/answer/admin/api/bridge-relay-groups/${groupId}/policy-rollback`,
    { release_id: releaseId, valid_for: validFor },
  );

export const useBridgeNodePools = () =>
  swr<BridgeNodePoolItem[]>('/answer/admin/api/bridge-node-pools');
export const createBridgeNodePool = (params: BridgeNodePoolSaveParams) =>
  request.post<BridgeNodePoolItem>(
    '/answer/admin/api/bridge-node-pools',
    params,
  );
export const updateBridgeNodePool = (
  id: string,
  params: BridgeNodePoolSaveParams,
) =>
  request.put<BridgeNodePoolItem>(
    `/answer/admin/api/bridge-node-pools/${id}`,
    params,
  );

export const useBridgeDomainPolicies = () =>
  swr<BridgeDomainPolicyItem[]>('/answer/admin/api/bridge-domain-policies');
export const createBridgeDomainPolicy = (
  params: BridgeDomainPolicySaveParams,
) =>
  request.post<BridgeDomainPolicyItem>(
    '/answer/admin/api/bridge-domain-policies',
    params,
  );
export const updateBridgeDomainPolicy = (
  id: string,
  params: BridgeDomainPolicySaveParams,
) =>
  request.put<BridgeDomainPolicyItem>(
    `/answer/admin/api/bridge-domain-policies/${id}`,
    params,
  );
export const validateBridgeDomainPolicy = (
  id: string,
  params: BridgeDomainPolicySaveParams,
) =>
  request.post<{ valid: boolean }>(
    `/answer/admin/api/bridge-domain-policies/${id || '_new'}/validate`,
    params,
  );
export const useBridgePolicySigningProfile = () =>
  swr<BridgePolicySigningProfile>(
    '/answer/admin/api/bridge-domain-policies/signing-profile',
  );

export const useBridgeAccessGrants = (params: {
  page: number;
  page_size: number;
  status?: string;
  keyword?: string;
}) =>
  swr<Type.ListResult<BridgeAccessGrantItem>>(
    `/answer/admin/api/bridge-access-grants?${qs.stringify(params, {
      skipNulls: true,
    })}`,
  );
export const createBridgeAccessGrant = (params: BridgeAccessGrantSaveParams) =>
  request.post<BridgeAccessGrantItem>(
    '/answer/admin/api/bridge-access-grants',
    params,
  );
export const updateBridgeAccessGrant = (
  id: string,
  params: BridgeAccessGrantSaveParams,
) =>
  request.put<BridgeAccessGrantItem>(
    `/answer/admin/api/bridge-access-grants/${id}`,
    params,
  );
export const revokeBridgeAccessGrant = (id: string) =>
  request.post(`/answer/admin/api/bridge-access-grants/${id}/revoke`);
export const getBridgeLoginBootstrapProfile = () =>
  request.get<BridgeLoginBootstrapEnvelope>(
    '/answer/admin/api/bridge-access-grants/login-bootstrap-profile',
  );

export const useBridgeNodeAudits = (params: {
  page: number;
  page_size: number;
  action?: string;
  object_type?: string;
  object_id?: string;
}) =>
  swr<Type.ListResult<BridgeNodeAuditItem>>(
    `/answer/admin/api/bridge-node-audits?${qs.stringify(params, {
      skipNulls: true,
    })}`,
  );
